<template>
    <Dashboard
        :title="__('Transaction edit', null, $page.props.profile_locale)"
    >

        <Heading
            :label="__('Transaction edit', null, $page.props.profile_locale)"
        />

        <div class="block font-bold text-gray-700">{{__('User', null, $page.props.profile_locale)}}</div>
        <div class="border rounded-md px-3 py-1.5 mb-2 bg-gray-100">
            {{transaction.user ? transaction.user.first_name + ' ' + transaction.user.last_name : __('Guest', null, $page.props.profile_locale)}}
        </div>

        <Select
            :label="__('Status', null, $page.props.profile_locale)"
            v-model="status"
            :data="statuses"
            id="status"
            :class="[
                status == 'sent' ? 'mb-3' : 'mb-10'
            ]"
        >
            <template #display="props">
                <span
                    class="px-1 rounded-md font-bold"
                    :class="{
                        ['bg-red-100 text-red-700'] : props.value == 'canceled',
                        ['bg-green-100 text-green-700'] : props.value == 'finished',
                        ['bg-yellow-100 text-yellow-700'] : props.value == 'sent',
                        ['bg-blue-100 text-blue-700'] : props.value == 'in_progress'
                    }"
                >
                    {{ __('message.' + props.value, null, $page.props.profile_locale) }}
                </span>
            </template>

            <template #default="props">
                <Option
                    v-for="(item, key) in props.data"
                    :key="key"
                    @click="props.clicked(item)"
                    class="flex justify-between"
                    :class="[
                        props.active != item ? 'hover:bg-gray-50' : '',
                    ]"
                    :selected="props.active == item"
                    :color="item == 'canceled' ? 'red' : (item == 'finished' ? 'green' : (item == 'in_progress' ? 'blue' : 'yellow'))"
                >
                    <span
                        class="px-1 rounded-md font-bold"
                    :class="{
                        ['bg-red-100 text-red-700'] : item == 'canceled',
                        ['bg-green-100 text-green-700'] : item == 'finished',
                        ['bg-yellow-100 text-yellow-700'] : item == 'sent',
                        ['bg-blue-100 text-blue-700'] : item == 'in_progress',
                    }"
                    >{{ __('message.' + item, null, $page.props.profile_locale) }}</span>
                </Option>
            </template>
        </Select>

        <div class="grid grid-cols-2 gap-2 mb-10" v-if="status == 'sent'">
            <Select
                :label="__('Courier', null, $page.props.profile_locale) + ' *'"
                :data="delivery"
                id="courier"
                v-model="form.courier"
            >
                <template #display="props">
                    <span class="px-1 rounded-md font-bold bg-yellow-100 text-yellow-700">{{ __(props.value.name) }}</span>
                </template>
                <template #default="props">
                    <Option
                        v-for="(item, key) in props.data"
                        :key="key"
                        @click="props.clicked(item)"
                        class="flex justify-between items-center"
                        color="yellow"
                        :selected="props.active && props.active.id == item.id"
                    >
                        <span class="px-1 rounded-md font-bold bg-yellow-100 text-yellow-700">{{ __(item.name) }}</span>
                        <img :src="item.image" width="50" height="40" class="object-contain" />
                    </Option>
                </template>
            </Select>
            <InputLabel
                id="tracking_id"
                v-model="form.tracking_id"
                :label="__('Tracking ID', null, $page.props.profile_locale) + ' *'"
            />
        </div>

        <div class="grid grid-cols-1 gap-2">
            <InputLabel
                id="billing.email"
                type="email"
                :label="__('Email', null, $page.props.profile_locale) + ' *'"
                v-model="form.userdata.email"
                autocomplete="email"
            />
            <div class="grid grid-cols-2 gap-2">
                <InputLabel
                    id="billing.name"
                    :label="__('Name or company name', null, $page.props.profile_locale) + ' *'"
                    v-model="form.userdata.name"
                    autocomplete="name"
                />
                <InputLabel
                    id="billing.contact_name"
                    :label="__('Name of the contact person', null, $page.props.profile_locale) + ' *'"
                    v-model="form.userdata.contact_name"
                    autocomplete="name"
                />
            </div>
            <InputLabel
                id="billing.address"
                :label="__('Street and number of the building / flat', null, $page.props.profile_locale) + ' *'"
                v-model="form.userdata.address"
                autocomplete="address"
            />
            <InputLabel
                id="billing.additional_address"
                :label="__('Additional address information', null, $page.props.profile_locale)"
                v-model="form.userdata.additional_address"
            />
            <div class="grid grid-cols-3 gap-2">
                <InputLabel
                    id="billing.phone"
                    :label="__('Phone', null, $page.props.profile_locale) + ' *'"
                    v-model="form.userdata.phone"
                    autocomplete="phone"
                />
                <InputLabel
                    id="billing.zip"
                    :label="__('Zip', null, $page.props.profile_locale) + ' *'"
                    v-model="form.userdata.zip"
                    autocomplete="zip"
                />
                <InputLabel
                    id="billing.city"
                    :label="__('City', null, $page.props.profile_locale) + ' *'"
                    v-model="form.userdata.city"
                    autocomplete="city"
                />
            </div>

            <Select
                :label="__('Country', null, $page.props.profile_locale) + ' *'"
                v-model="form.userdata.country"
                :data="countries"
                id="billing.country"
            >
                <template #display="props">
                    <div class="flex items-center">
                        {{ __('countries.' + props.value) }}
                        <img class="mx-1 w-6 h-6 max-w-max" :src="getLangImage(props.value)">
                    </div>
                </template>
                <template #default="props">
                    <Option
                        v-for="(item, key) in props.data"
                        :key="key"
                        @click="props.clicked(key)"
                        class="flex justify-between"
                        color="yellow"
                        :selected="props.active == key"
                    >
                        {{ __('countries.' + key) }}
                        <img class="mx-1 w-6 h-6 max-w-max" :src="getLangImage(key)">
                    </Option>
                </template>
            </Select>
        </div>

        <BreezeButton
            padding="md"
            class="mt-3"
            @click="editTransaction"
        >
        {{__('Save', null, $page.props.profile_locale)}}
        </BreezeButton>

    </Dashboard>
</template>

<script>
    import { ref } from 'vue';
    import Dashboard from '@/Layouts/Dashboard';
    import InputLabel from 'UI/InputLabel';
    import BreezeButton from 'UI/Button';
    import SvgIcon from 'UI/SvgIcon';
    import ImageUpload from 'UI/ImageUpload';
    import { useForm } from '@inertiajs/inertia-vue3';
    import CustomSelect from 'UI/CustomSelect';
    import Heading from 'COMP/Heading';
    import Select from 'COMP/CustomSelect/Select';
    import Option from 'COMP/CustomSelect/Option';

    import {
        getLangImage,
    } from 'helper';

    export default {
        props: {
            transaction: Object,
            delivery: Object,
            countries: [Object, Array]
        },
        setup(props){
            const shipment = ref(props.transaction.shipping);

            const statuses = ref([
                'in_progress',
                'canceled',
                'sent',
                'finished'
            ]);
            const status = ref(props.transaction.status);

            const form = useForm({
                userdata: props.transaction.user_data,
                transaction: props.transaction,
                courier: props.transaction.shipping,
                tracking_id: props.transaction.tracking_id,
                status: status,
            })

            const editTransaction = () => {
                form.post(route('update.transaction', props.transaction.id));
            }

            return {
                shipment,
                statuses,
                status,
                form,
                editTransaction,
                getLangImage
            }
        },
        components: {
            Dashboard,
            BreezeButton,
            InputLabel,
            SvgIcon,
            ImageUpload,
            CustomSelect,
            Heading,
            Select,
            Option,
        }

    }
</script>

